import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, Check, Star, Briefcase, Shield, FileText, User,
  X, CheckCircle2, Loader2, Lock, ArrowRight, TicketPercent,
} from 'lucide-react';
import { trackProInterestRegistered } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FREE_FEATURES = [
  'Perfil de alta en Providers',
  'Acceso a 3 oportunidades de trabajo por mes',
  'Perfil profesional básico sin calificaciones',
  'Sin herramientas extra',
];

const PRO_FEATURES = [
  'Perfil verificado con calificaciones',
  'Acceso a +30 oportunidades trabajo mensuales',
  'Herramientas para hacer presupuestos profesionales',
  'Perfil destacado que construye reputación y confianza',
  'Acceso a futuras herramientas y descuentos exclusivos en herramientas y materiales',
];

const BENEFITS = [
  {
    Icon: Briefcase,
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-500',
    title: 'Acceso a más oportunidades',
    text: 'Accedé a más de 30 oportunidades de trabajo posibles por mes y aumentá tus posibilidades de encontrar trabajos que se adapten a tus servicios.',
  },
  {
    Icon: Shield,
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-500',
    title: 'Un perfil que genera más confianza',
    text: 'Mostrá a los usuarios que tu perfil cuenta con la documentación presentada a Providers y destacate como un profesional preparado para trabajar.',
  },
  {
    Icon: FileText,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-500',
    title: 'Presupuestos y facturas profesionales',
    text: 'Generá presupuestos con una presentación profesional y enviáselos directamente a tus clientes. Presentarte mejor puede ayudarte a concretar más trabajos.',
  },
  {
    Icon: User,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-500',
    title: 'Mostrá mejor lo que hacés',
    text: 'Completá tu perfil con tus servicios, medios de pago y herramientas profesionales para que los usuarios tengan más información antes de contactarte.',
  },
];

export default function ProvidersPro() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('ticketId');

  const [submitting, setSubmitting] = useState(false);
  const [interestRegistered, setInterestRegistered] = useState(false);

  // Guard: sin sesión de proveedor, volver al login
  useEffect(() => {
    if (!localStorage.getItem('provider_token')) {
      navigate('/provider-auth', { replace: true });
    }
  }, [navigate]);

  const finish = () =>
    navigate(ticketId ? `/profesionales?autoReveal=${ticketId}` : '/profesionales', { replace: true });

  const handleInterest = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('provider_token');
      await axios.post(`${API}/providers/pro-interest`, {}, { headers: { Authorization: `Bearer ${token}` } });
      trackProInterestRegistered();
      setInterestRegistered(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No pudimos registrar tu interés. Intentá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="providers-pro-page">
      {/* Encabezado oscuro */}
      <header className="bg-slate-900 px-5 pt-8 pb-8 rounded-b-3xl">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-amber-400" fill="currentColor" />
            <span className="text-xs font-bold uppercase tracking-widest text-amber-400">Próximamente</span>
            <Zap className="w-4 h-4 text-amber-400" fill="currentColor" />
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-3">Providers PRO</h1>
          <p className="text-sm text-slate-300 leading-relaxed mb-5">
            Más herramientas, más oportunidades y un perfil más profesional para ayudarte a concretar más trabajos.
          </p>
          <span className="inline-block rounded-full border border-amber-400/60 px-5 py-2.5 text-sm font-semibold text-amber-400">
            Hoy no tenés que pagar nada
          </span>
        </div>
      </header>

      <main className="flex-1 px-5 py-6">
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Comparación Gratis vs PRO */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            {/* Gratis */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Gratis</p>
              <h3 className="text-lg font-extrabold text-gray-900 mt-1">Providers</h3>
              <p className="text-sm text-gray-400 mt-1 leading-snug">Todo lo necesario para empezar.</p>
              <ul className="mt-4 space-y-2.5">
                {FREE_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-sm text-gray-700 leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* PRO */}
            <div className="relative rounded-2xl border-2 border-slate-900 bg-slate-900 p-4">
              <span className="absolute top-3 right-3 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-900">
                Próximamente
              </span>
              <p className="text-xs font-bold uppercase tracking-wide text-amber-400">Pro</p>
              <h3 className="text-lg font-extrabold text-white mt-1">Providers PRO</h3>
              <p className="text-sm text-slate-400 mt-1 leading-snug">Más herramientas para crecer.</p>

              <div className="mt-3">
                <span className="text-2xl font-extrabold text-white">US$7</span>
                <span className="text-sm text-slate-400"> / mes</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="rounded-md bg-green-500/20 px-1.5 py-0.5 text-[10px] font-bold text-green-400">
                  AHORRÁS 20%
                </span>
                <span className="text-xs text-slate-400">US$67 / año</span>
              </div>

              <ul className="mt-4 space-y-2.5">
                {PRO_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" />
                    <span className="text-sm text-slate-200 leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Beneficios */}
          <section>
            <h2 className="text-center text-lg font-bold text-gray-900 mb-4">¿Qué obtenés con PRO?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {BENEFITS.map(({ Icon, iconBg, iconColor, title, text }) => (
                <div key={title} className="flex gap-3 rounded-2xl border border-gray-200 bg-white p-4">
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg} ${iconColor}`}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-gray-900 leading-tight">{title}</h3>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA principal / confirmación */}
          <section className="max-w-4xl mx-auto overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.08)] text-center">
            {interestRegistered ? (
              <div className="px-6 py-10 md:px-16" data-testid="pro-interest-confirmation">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-gray-900">¡Listo!</h2>
                <p className="max-w-lg mx-auto text-base text-gray-500 mt-2 leading-relaxed">
                  Registramos tu interés en Providers PRO. Cuando esté disponible, te vamos a avisar y vas a recibir
                  tu 20% OFF para suscribirte.
                </p>
                <button
                  type="button"
                  onClick={finish}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: 'var(--primary-strong)' }}
                >
                  Volver a Providers
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="px-6 pt-10 pb-9 md:px-16">
                  <h2 className="text-3xl font-extrabold text-slate-900 leading-tight">
                    ¿Te interesa Providers PRO?
                  </h2>
                  <p className="max-w-xl mx-auto text-xl text-gray-500 mt-3 leading-relaxed">
                    Obtené un <strong className="text-green-600">20% de descuento</strong> cuando <br className="hidden sm:block" />
                    Providers PRO esté disponible!
                  </p>

                  <button
                    type="button"
                    data-testid="pro-interest-btn"
                    onClick={handleInterest}
                    disabled={submitting}
                    className="group flex w-full max-w-2xl mx-auto mt-8 min-h-24 overflow-hidden rounded-3xl border-2 border-lime-500 bg-slate-950 text-left shadow-[0_8px_20px_rgba(15,23,42,0.18)] transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    <span className="flex w-28 flex-shrink-0 items-center justify-center bg-lime-500 text-slate-950 transition-colors group-hover:bg-lime-400 sm:w-36">
                      {submitting ? <Loader2 className="w-10 h-10 animate-spin" /> : <ArrowRight className="w-12 h-12" strokeWidth={2.5} />}
                    </span>
                    <span className="flex flex-1 items-center justify-center px-4 text-center text-2xl font-extrabold uppercase text-white sm:text-4xl">
                      Quiero mi <span className="ml-2 text-lime-500">20%OFF</span>
                    </span>
                  </button>

                  <p className="mt-7 flex items-center justify-center gap-3 text-base font-semibold text-slate-700">
                    <Lock className="h-6 w-6 flex-shrink-0 text-slate-700" />
                    No se realizará ningún cobro. Solo registramos tu interés.
                  </p>
                </div>

                <div className="border-t border-gray-100 px-6 py-7 md:px-16">
                  <button
                    type="button"
                    data-testid="pro-skip-btn"
                    onClick={finish}
                    className="inline-flex items-center gap-3 text-lg text-gray-500 transition-colors hover:text-gray-700"
                  >
                    <X className="h-7 w-7 text-gray-400" strokeWidth={1.5} />
                    Prefiero seguir con Providers Gratis
                  </button>

                  <div className="mx-auto mt-7 flex max-w-2xl items-center justify-center gap-3 rounded-2xl border border-lime-600/50 bg-lime-50 px-4 py-4 text-slate-900">
                    <TicketPercent className="h-9 w-9 flex-shrink-0 text-green-600" />
                    <span className="text-lg font-bold leading-tight sm:text-2xl">
                      Quedan <strong className="mx-1 inline-flex rounded-lg bg-green-600 px-2 py-1 text-white">20</strong> cupones de <span className="text-green-700">20%OFF</span>
                    </span>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
